import { ref } from 'vue'
import { useShopwareContext, useI18n } from '#imports'
import { createSharedComposable } from '@vueuse/core'
import type { MollieCtor, MollieInstance, MollieMandate } from '~/types/mollie'

/** Sentinel `selectedMandateId` value meaning "enter a new card" rather than reuse a mandate. */
export const NEW_CARD = 'newCard'

const MOLLIE_SDK_URL = 'https://js.mollie.com/v1/mollie.js'

function deriveLocale(language: string | undefined | null): string {
    if (!language) return 'en_GB'
    return language.replace('-', '_')
}

async function loadMollieSdk(): Promise<MollieCtor> {
    if (typeof window === 'undefined') {
        throw new Error('Mollie SDK can only be loaded in the browser')
    }
    // window.Mollie is shared with the v2 express SDK, which exposes an object rather than a
    // callable, so narrow to the v1 constructor before reusing an already loaded SDK.
    if (typeof window.Mollie === 'function') return window.Mollie

    await new Promise<void>((resolve, reject) => {
        const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${MOLLIE_SDK_URL}"]`)
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve())
            existingScript.addEventListener('error', () => reject(new Error('Failed to load Mollie SDK')))
            return
        }
        const script = document.createElement('script')
        script.src = MOLLIE_SDK_URL
        script.async = true
        script.addEventListener('load', () => resolve())
        script.addEventListener('error', () => reject(new Error('Failed to load Mollie SDK')))
        document.head.appendChild(script)
    })

    if (typeof window.Mollie !== 'function') throw new Error('Mollie SDK loaded but window.Mollie is not the v1 constructor')
    return window.Mollie
}

function _useMollie() {
    const { apiClient } = useShopwareContext()
    const i18n = useI18n()
    const localeProperties = (i18n as unknown as { localeProperties?: { value?: { language?: string } } }).localeProperties
    const language = localeProperties?.value?.language
    const locale = deriveLocale(language)

    const mollie = ref<MollieInstance | null>(null)
    // Gateway-side "One-Click Payment" admin toggle, surfaced from the config fetch.
    // When off the gateway refuses to store/list saved cards, so the save feature is hidden.
    const oneClickPayments = ref(false)
    // Customer opt-in for the "save this card" checkbox; a saved card (mandate) is only
    // minted as a side-effect of the successful payment when this is true (ADR-0006).
    const shouldSaveCard = ref(false)
    // Saved cards (mandates) held at the gateway for the logged-in customer, rendered as the
    // one-click picker at checkout. Populated by fetchMandates(); empty until then.
    const mandates = ref<MollieMandate[]>([])
    // Which card the shopper picks in the credit-card step: a real mandate id (reuse) or the
    // `NEW_CARD` sentinel (enter a fresh card). Defaults to NEW_CARD until mandates load.
    const selectedMandateId = ref<string>(NEW_CARD)
    let initPromise: Promise<void> | null = null

    function init(): Promise<void> {
        if (initPromise) return initPromise
        initPromise = (async () => {
            // Plugin response also includes `locale` (saleschannel default) and `oneClickPayments`.
            // We deliberately prefer the user-selected i18n locale — an English-speaking visitor
            // on all4golf.de should still see the Mollie iframe in English.
            const { data } = await apiClient.invoke('readMollieConfig get /mollie/config')
            oneClickPayments.value = Boolean(data.oneClickPayments)
            const MollieCtor = await loadMollieSdk()
            mollie.value = MollieCtor(data.profileId, { locale, testmode: data.testMode })
        })()
        return initPromise
    }

    /**
     * Fetch the customer's saved cards (mandates) and pre-select one for reuse. Pre-selects
     * the first mandate when any exist, else the `NEW_CARD` sentinel. The one-click + logged-in
     * + non-guest gate lives at the call site (MollieCard). On any error we resolve to an empty
     * list and NEW_CARD — a failed list must never block the plain new-card form (ADR-0006).
     */
    async function fetchMandates(customerId: string): Promise<void> {
        try {
            const { data } = await apiClient.invoke('readMollieMandates get /mollie/mandates/{customerId}', {
                pathParams: { customerId },
            })
            mandates.value = data.mandates ?? []
            selectedMandateId.value = mandates.value[0]?.id ?? NEW_CARD
        } catch {
            mandates.value = []
            selectedMandateId.value = NEW_CARD
        }
    }

    /**
     * Revoke (delete) a saved card at the gateway and reconcile local state (A4G-3141).
     * On success removes the mandate from `mandates` and, if it was the selected card,
     * re-selects the first remaining mandate — or the `NEW_CARD` sentinel when none remain.
     * On failure (thrown request or `success: false`) leaves `mandates` and
     * `selectedMandateId` untouched and returns `false` so the caller can notify.
     */
    async function revokeMandate(customerId: string, mandateId: string): Promise<boolean> {
        try {
            const { data } = await apiClient.invoke('revokeMollieMandate post /mollie/mandate/revoke/{customerId}/{mandateId}', {
                pathParams: { customerId, mandateId },
            })
            if (!data.success) return false
        } catch {
            return false
        }
        mandates.value = mandates.value.filter((mandate) => mandate.id !== mandateId)
        if (selectedMandateId.value === mandateId) {
            selectedMandateId.value = mandates.value[0]?.id ?? NEW_CARD
        }
        return true
    }

    return {
        mollie,
        locale,
        oneClickPayments,
        shouldSaveCard,
        mandates,
        selectedMandateId,
        fetchMandates,
        revokeMandate,
        init,
    }
}

// Shared across the field renderer and the place-order orchestrator so they use one SDK
// instance and one piece of save-card state. `createSharedComposable` is a client-only
// optimisation (it short-circuits to a plain pass-through on the server — see VueUse
// `if (!isClient) return composable`), so the shared cache only ever exists per-browser
// and cannot leak between server requests. Matches the Shopware composables (useCart etc.).
export const useMollie = createSharedComposable(_useMollie)
